import { useEffect, useState } from "react";
import { Palette, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const PRESETS = [
  { name: "Classic", fg: "#0F172A", bg: "#FFFFFF" },
  { name: "Royal", fg: "#2563EB", bg: "#FFFFFF" },
  { name: "Electric", fg: "#06B6D4", bg: "#FFFFFF" },
  { name: "Violet", fg: "#7C3AED", bg: "#FFFFFF" },
  { name: "Emerald", fg: "#10B981", bg: "#FFFFFF" },
  { name: "Sunset", fg: "#F59E0B", bg: "#FFF7ED" },
  { name: "Cherry", fg: "#DC2626", bg: "#FEF2F2" },
  { name: "Midnight", fg: "#FFFFFF", bg: "#0F172A" },
];

const STYLES = [
  { key: "square", label: "Square" },
  { key: "rounded", label: "Rounded" },
  { key: "dots", label: "Dots" },
];

export default function QRStylePanel({ campaign, onChanged }) {
  const [fg, setFg] = useState(campaign?.qr_fg_color || "#0F172A");
  const [bg, setBg] = useState(campaign?.qr_bg_color || "#FFFFFF");
  const [style, setStyle] = useState(campaign?.qr_style || "square");
  const [logo, setLogo] = useState(campaign?.qr_logo_url || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (campaign) {
      setFg(campaign.qr_fg_color || "#0F172A");
      setBg(campaign.qr_bg_color || "#FFFFFF");
      setStyle(campaign.qr_style || "square");
      setLogo(campaign.qr_logo_url || "");
    }
  }, [campaign?.id]);

  const dirty =
    fg !== (campaign?.qr_fg_color || "#0F172A") ||
    bg !== (campaign?.qr_bg_color || "#FFFFFF") ||
    style !== (campaign?.qr_style || "square") ||
    logo !== (campaign?.qr_logo_url || "");

  const apply = async () => {
    setSaving(true);
    try {
      await api.patch(`/campaigns/${campaign.id}`, {
        qr_fg_color: fg,
        qr_bg_color: bg,
        qr_style: style,
        qr_logo_url: logo || "",
      });
      toast.success("QR style updated");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8" data-testid="qr-style-panel">
      <div className="flex items-center gap-2 mb-4">
        <Palette className="w-5 h-5 text-slate-500" />
        <h2 className="text-lg font-bold text-slate-900">Customise QR style</h2>
      </div>
      <p className="text-sm text-slate-500 mb-6">
        Match the QR to your brand. The printed image stays scannable at every size.
      </p>

      {/* Presets */}
      <div className="mb-6">
        <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2 block">
          Colour preset
        </Label>
        <div className="grid grid-cols-4 sm:grid-cols-8 gap-2" data-testid="qr-style-presets">
          {PRESETS.map((p) => {
            const active = p.fg === fg && p.bg === bg;
            return (
              <button
                key={p.name}
                onClick={() => {
                  setFg(p.fg);
                  setBg(p.bg);
                }}
                title={p.name}
                data-testid={`qr-preset-${p.name.toLowerCase()}`}
                className={`aspect-square rounded-xl border-2 transition-all ${
                  active ? "border-blue-600 scale-105" : "border-slate-200 hover:border-slate-300"
                }`}
                style={{ background: p.bg }}
              >
                <div
                  className="w-full h-full rounded-lg grid place-items-center"
                  style={{ color: p.fg }}
                >
                  <div className="w-4 h-4 rounded-sm" style={{ background: p.fg }} />
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Custom colors */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">
            Foreground
          </Label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={fg}
              onChange={(e) => setFg(e.target.value.toUpperCase())}
              className="w-11 h-11 rounded-lg border border-slate-300 cursor-pointer bg-white"
              data-testid="qr-fg-color-picker"
            />
            <Input
              value={fg}
              onChange={(e) => setFg(e.target.value.toUpperCase())}
              maxLength={7}
              className="h-11 rounded-lg font-mono text-sm"
              data-testid="qr-fg-color-input"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">
            Background
          </Label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={bg}
              onChange={(e) => setBg(e.target.value.toUpperCase())}
              className="w-11 h-11 rounded-lg border border-slate-300 cursor-pointer bg-white"
              data-testid="qr-bg-color-picker"
            />
            <Input
              value={bg}
              onChange={(e) => setBg(e.target.value.toUpperCase())}
              maxLength={7}
              className="h-11 rounded-lg font-mono text-sm"
              data-testid="qr-bg-color-input"
            />
          </div>
        </div>
      </div>

      {/* Corner style */}
      <div className="mb-6">
        <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2 block">
          Module style
        </Label>
        <div className="grid grid-cols-3 gap-2" data-testid="qr-style-selector">
          {STYLES.map((s) => (
            <button
              key={s.key}
              onClick={() => setStyle(s.key)}
              data-testid={`qr-style-${s.key}`}
              className={`px-4 py-3 rounded-xl border text-sm font-semibold transition-all ${
                style === s.key
                  ? "border-blue-600 bg-blue-50 text-blue-700"
                  : "border-slate-200 hover:border-slate-300 text-slate-700"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Logo URL */}
      <div className="mb-6">
        <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">
          Logo image URL <span className="text-slate-400 normal-case font-normal">(optional)</span>
        </Label>
        <Input
          value={logo}
          onChange={(e) => setLogo(e.target.value)}
          placeholder="https://yourbrand.com/logo.png"
          data-testid="qr-logo-input"
          maxLength={500}
          className="h-11 rounded-xl font-mono text-sm"
        />
        <p className="text-xs text-slate-500 mt-1.5">
          Small logo drops into the QR centre with a safe padding so it still scans. Use a PNG with a transparent background.
        </p>
      </div>

      <div className="flex justify-end">
        <Button
          onClick={apply}
          disabled={!dirty || saving}
          data-testid="qr-style-apply-btn"
          className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold px-5"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply style"}
        </Button>
      </div>
    </div>
  );
}
